import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Target, 
  Users, 
  Lightbulb, 
  ArrowRight, 
  Star, 
  Clock, 
  CheckCircle,
  Play,
  Map,
  Mountain,
  Settings,
  Layers,
  Search,
  GitBranch,
  Database
} from 'lucide-react';

interface LearningPathway {
  id: string;
  title: string;
  description: string;
  duration: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  category: 'mof' | 'frameworks' | 'tools' | 'collaboration';
  icon: React.ReactNode;
  color: string;
  steps: string[];
  outcomes: string[];
  prerequisites?: string[];
}

interface JourneyOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  path: string;
  category: string;
}

export const LearningPathways: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'mof' | 'frameworks' | 'tools' | 'collaboration'>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<'all' | 'beginner' | 'intermediate' | 'advanced'>('all');

  // Journey options from Mission Control
  const journeyOptions: JourneyOption[] = [
    {
      id: 'island-explorer',
      title: 'Island Explorer',
      description: 'Discover and navigate capabilities within the system',
      icon: <Map className="h-8 w-8" />,
      color: 'sage',
      path: '/elevation/island',
      category: 'exploration'
    },
    {
      id: 'mountain-ascent',
      title: 'Mountain Ascent',
      description: 'Track your progress and climb towards mastery',
      icon: <Mountain className="h-8 w-8" />,
      color: 'primary',
      path: '/elevation/mountain',
      category: 'progress'
    },
    {
      id: 'engines',
      title: 'Engines',
      description: 'Infrastructure powering the system',
      icon: <Settings className="h-8 w-8" />,
      color: 'purple',
      path: '/elevation/engines',
      category: 'infrastructure'
    },
    {
      id: 'metadata',
      title: 'M3: Metadata',
      description: 'Universal language for models',
      icon: <Layers className="h-8 w-8" />,
      color: 'indigo',
      path: '/elevation/grammar',
      category: 'mof'
    },
    {
      id: 'models',
      title: 'M2: Models',
      description: 'Registry of frameworks and systems',
      icon: <Search className="h-8 w-8" />,
      color: 'primary',
      path: '/elevation/models',
      category: 'mof'
    },
    {
      id: 'templates',
      title: 'M1: Templates',
      description: 'Ready-to-deploy patterns and designs',
      icon: <GitBranch className="h-8 w-8" />,
      color: 'sage',
      path: '/elevation/templates',
      category: 'mof'
    },
    {
      id: 'runtime',
      title: 'M0: Runtime',
      description: 'Actual experiences with measurable outcomes',
      icon: <Database className="h-8 w-8" />,
      color: 'charcoal',
      path: '/elevation/runtime',
      category: 'mof'
    }
  ];

  // Learning pathways
  const learningPathways: LearningPathway[] = [
    {
      id: 'mof-fundamentals',
      title: 'MOF Architecture Fundamentals',
      description: 'Master the four-layer Meta-Object Facility architecture that powers the entire system',
      duration: '2-3 hours',
      difficulty: 'beginner',
      category: 'mof',
      icon: <Layers className="h-6 w-6" />,
      color: 'text-[var(--text-ter)]',
      steps: [
        'Understand the four-layer knowledge architecture (M3 → M2 → M1 → M0)',
        'Explore the Meta-Stack grammar (Ontology, Modality, Dynamics, Evaluation)',
        'Navigate the registry of M2 models',
        'Examine M1 templates and their relationship to models',
        'Review M0 runtime instances and outcomes'
      ],
      outcomes: [
        'Clear understanding of system architecture',
        'Ability to navigate between MOF layers',
        'Knowledge of how models become templates become experiences'
      ]
    },
    {
      id: 'degap-mastery',
      title: 'DEGAP Framework Mastery',
      description: 'Learn to apply the Five Stages of DEGAP for effective decision-making and prioritization',
      duration: '3-4 hours',
      difficulty: 'intermediate',
      category: 'frameworks',
      icon: <Target className="h-6 w-6" />,
      color: 'text-consulting-slate-blue-600',
      steps: [
        'Study the Five Stages: Decide, Engage, Gather, Arrange, Prioritize',
        'Practice with real-world scenarios',
        'Learn to identify which stage you\'re in',
        'Master the transitions between stages',
        'Apply DEGAP to your own challenges'
      ],
      outcomes: [
        'Confident application of DEGAP in daily work',
        'Improved decision-making speed and quality',
        'Better team alignment and communication'
      ],
      prerequisites: ['MOF Architecture Fundamentals']
    },
    {
      id: 'awareness-frameworks',
      title: 'Advanced Awareness Frameworks',
      description: 'Master the Johari Window, Logical Levels, and Ladder of Inference for deeper self-awareness',
      duration: '4-5 hours',
      difficulty: 'advanced',
      category: 'frameworks',
      icon: <Lightbulb className="h-6 w-6" />,
      color: 'text-[var(--mustard)]',
      steps: [
        'Explore the Johari Window for self-awareness',
        'Apply Logical Levels for change and development',
        'Use the Ladder of Inference for belief formation',
        'Practice integrating all three frameworks',
        'Develop personal awareness strategies'
      ],
      outcomes: [
        'Enhanced self-awareness and interpersonal skills',
        'Better understanding of how beliefs form',
        'Improved ability to facilitate change'
      ],
      prerequisites: ['DEGAP Framework Mastery']
    },
    {
      id: 'collaboration-tools',
      title: 'Team Collaboration & Facilitation',
      description: 'Learn to use the system\'s tools for effective team collaboration and workshop facilitation',
      duration: '2-3 hours',
      difficulty: 'intermediate',
      category: 'collaboration',
      icon: <Users className="h-6 w-6" />,
      color: 'text-[var(--text-ter)]',
      steps: [
        'Set up team workspaces and permissions',
        'Learn to facilitate workshops using templates',
        'Master the art of collaborative decision-making',
        'Use analytics to track team progress',
        'Share success stories and learnings'
      ],
      outcomes: [
        'Confident workshop facilitation skills',
        'Improved team collaboration processes',
        'Ability to track and measure team progress'
      ]
    },
    {
      id: 'system-administration',
      title: 'System Administration & Customization',
      description: 'Learn to customize the system, manage users, and configure advanced features',
      duration: '3-4 hours',
      difficulty: 'advanced',
      category: 'tools',
      icon: <Settings className="h-6 w-6" />,
      color: 'text-[var(--text-sec)]',
      steps: [
        'Configure system settings and preferences',
        'Manage user roles and permissions',
        'Customize templates and frameworks',
        'Set up analytics and reporting',
        'Troubleshoot common issues'
      ],
      outcomes: [
        'Full system administration capabilities',
        'Ability to customize for organizational needs',
        'Confident troubleshooting and support skills'
      ],
      prerequisites: ['MOF Architecture Fundamentals', 'Team Collaboration & Facilitation']
    }
  ];

  const filteredPathways = learningPathways.filter(pathway => {
    const categoryMatch = selectedCategory === 'all' || pathway.category === selectedCategory;
    const difficultyMatch = selectedDifficulty === 'all' || pathway.difficulty === selectedDifficulty;
    return categoryMatch && difficultyMatch;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
      case 'intermediate': return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
      case 'advanced': return 'bg-[var(--coral-dim)] text-[var(--coral)]';
      default: return 'bg-[var(--hover-tint)] text-[var(--text-pri)]';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'mof': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      case 'frameworks': return 'bg-consulting-slate-blue-100 text-consulting-slate-blue-800';
      case 'tools': return 'bg-[var(--hover-tint)] text-[var(--text-pri)]';
      case 'collaboration': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      default: return 'bg-[var(--hover-tint)] text-[var(--text-pri)]';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <BookOpen className="h-12 w-12 text-consulting-slate-blue-600" />
        <div>
          <h1 className="text-4xl font-bold text-charcoal-900">Learning Pathways</h1>
          <p className="text-lg text-charcoal-600">Structured learning journeys to master the Peak Priorities system</p>
        </div>
      </div>

      {/* Journey Options Section */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 p-8 border-2 border-consulting-slate-blue-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Choose Your Journey</h2>
        <p className="text-charcoal-700 mb-6">
          Start your learning journey by exploring different areas of the system. Each path offers unique insights and skills.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {journeyOptions.map((option) => (
            <Link
              key={option.id}
              to={option.path}
              className="bg-[var(--bg-raised)] p-4 border border-[var(--rule)] hover:shadow-md hover:border-consulting-slate-blue-300 transition-all group"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2  ${
                  option.color === 'sage' ? 'bg-sage-100 text-sage-600' :
                  option.color === 'primary' ? 'bg-consulting-slate-blue-100 text-consulting-slate-blue-600' :
                  option.color === 'purple' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' :
                  option.color === 'indigo' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' :
                  'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                }`}>
                  {option.icon}
                </div>
                <h3 className="font-bold text-charcoal-900 group-hover:text-consulting-slate-blue-700 transition-colors">
                  {option.title}
                </h3>
              </div>
              <p className="text-sm text-charcoal-600">{option.description}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Learning Pathways Section */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-charcoal-900">Structured Learning Pathways</h2>
          <div className="flex gap-4">
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value as any)}
              className="px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
            >
              <option value="all">All Categories</option>
              <option value="mof">MOF Architecture</option>
              <option value="frameworks">Frameworks</option>
              <option value="tools">Tools</option>
              <option value="collaboration">Collaboration</option>
            </select>
            
            <select 
              value={selectedDifficulty} 
              onChange={(e) => setSelectedDifficulty(e.target.value as any)}
              className="px-3 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
            >
              <option value="all">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
        </div>

        <div className="space-y-6">
          {filteredPathways.map((pathway) => (
            <div key={pathway.id} className="border border-[var(--rule)] p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2  bg-[var(--hover-tint)] ${pathway.color}`}>
                    {pathway.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-charcoal-900">{pathway.title}</h3>
                    <p className="text-charcoal-600">{pathway.description}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getDifficultyColor(pathway.difficulty)}`}>
                    {pathway.difficulty}
                  </span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(pathway.category)}`}>
                    {pathway.category}
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div>
                  <h4 className="font-semibold text-charcoal-900 mb-2 flex items-center gap-2">
                    <Play className="h-4 w-4" />
                    Learning Steps
                  </h4>
                  <ul className="space-y-1">
                    {pathway.steps.map((step, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-charcoal-700">
                        <span className="flex-shrink-0 w-5 h-5 bg-consulting-slate-blue-100 text-consulting-slate-blue-600 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                          {index + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-charcoal-900 mb-2 flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Learning Outcomes
                  </h4>
                  <ul className="space-y-1">
                    {pathway.outcomes.map((outcome, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-charcoal-700">
                        <CheckCircle className="h-4 w-4 text-[var(--green)] flex-shrink-0 mt-0.5" />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-[var(--rule)]">
                <div className="flex items-center gap-4 text-sm text-[var(--text-sec)]">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{pathway.duration}</span>
                  </div>
                  {pathway.prerequisites && (
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4" />
                      <span>Prerequisites: {pathway.prerequisites.join(', ')}</span>
                    </div>
                  )}
                </div>
                <button className="flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors">
                  <span>Start Pathway</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
